import { useEffect, useRef, useState } from "react";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";

export const cleanNip = (v) => (v || "").replace(/\D/g, "");

const fmtDate = (v) => {
  if (!v) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
};
export { fmtDate };

export default function CompanyLookup({ nip, onFill, testId = "company-lookup", auto = false, by = "nip", onStatus }) {
  const { t } = useT();
  const L = t.lookup;
  const [state, setState] = useState({ loading: false, result: null, error: "" });
  const digits = cleanNip(nip);
  const ready = by === "regon" ? digits.length === 9 || digits.length === 14 : digits.length === 10;
  const lastAuto = useRef(auto ? digits : "");

  const run = async () => {
    setState({ loading: true, result: null, error: "" });
    onStatus?.("loading");
    try {
      const r = await api.get(by === "regon" ? `/company/regon/${digits}` : `/company/${digits}`);
      if (!r.data.found) { onStatus?.("not_found"); return setState({ loading: false, result: null, error: by === "regon" ? L.not_found_regon || L.not_found : L.not_found }); }
      onFill(r.data);
      onStatus?.("found");
      setState({ loading: false, result: r.data, error: "" });
    } catch (e) {
      const d = e.response?.data?.detail;
      const st = e.response?.status;
      if (st === 424 || !e.response) onStatus?.("unavailable");
      else onStatus?.("error");
      setState({ loading: false, result: null, error: st === 429 ? t.errors.too_many : typeof d === "string" ? d : L.err });
    }
  };

  useEffect(() => {
    if (!auto || !ready || lastAuto.current === digits) return undefined;
    const id = setTimeout(() => { lastAuto.current = digits; run(); }, 400);
    return () => clearTimeout(id);
  }, [auto, ready, digits]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid={testId} className="mt-2">
      <button
        type="button"
        data-testid={`${testId}-button`}
        onClick={run}
        disabled={!ready || state.loading}
        title={ready ? "" : by === "regon" ? L.hint_regon || L.hint : L.hint}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-teal hover:text-brand-tealDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {state.loading ? <Loader2 size={13} className="animate-spin" /> : <Building2 size={13} />} {state.loading ? L.loading : L.button}
      </button>
      {state.result && (
        <div data-testid={`${testId}-result`} className="mt-2 rounded-lg border border-brand-teal/25 bg-brand-teal/5 px-3 py-2 text-xs leading-relaxed">
          <p className="inline-flex items-start gap-1.5 font-semibold text-foreground">
            <CheckCircle2 size={13} className="text-brand-teal shrink-0 mt-0.5" />
            <span>{L.filled} <span>{state.result.name}</span></span>
          </p>
          <ul className="mt-1 space-y-0.5 text-muted-foreground pl-[18px]">
            {state.result.vat_status && <li data-testid={`${testId}-vat`}>{L.vat.replace("{status}", state.result.vat_status)}</li>}
            {state.result.start_date && <li data-testid={`${testId}-regdate`}>{L.reg_date.replace("{date}", fmtDate(state.result.start_date))}</li>}
            {(state.result.vat_checked || state.result.gus_checked) && <li className="text-brand-teal/90">{L.checked_on.replace("{date}", fmtDate(state.result.vat_checked) || fmtDate(new Date().toISOString()))}</li>}
            {state.result.ended && <li className="text-brand-red font-semibold">{L.ended_warn}</li>}
            {state.result.sources?.gus === "test" && <li>{L.test_env}</li>}
          </ul>
        </div>
      )}
      {state.error && <p data-testid={`${testId}-error`} className="mt-1.5 text-xs text-brand-red">{state.error}</p>}
    </div>
  );
}
